/**
 * Biometric unlock via WebAuthn PRF.
 *
 * The passphrase is the root secret that unseals the identity keystore
 * (HIGH-06). Typing it on every reload works but gets tedious on
 * mobile. This module layers a convenience unlock on top:
 *
 *   - At enrollment time we create a discoverable platform
 *     authenticator credential (Touch ID / Face ID / Windows Hello /
 *     Android biometric / device PIN).
 *   - The PRF (pseudo-random function) WebAuthn extension gives us a
 *     deterministic 32-byte secret for a fixed `eval` input, gated by
 *     a successful biometric check. We use that secret as an
 *     AES-256-GCM key to wrap the passphrase.
 *   - On next load we prompt WebAuthn, derive the same PRF output,
 *     unwrap the passphrase, and hand it to the existing sealed-store
 *     unlock flow. The user sees "Touch ID …" and is in.
 *
 * Security model:
 *   - The PRF secret lives inside the authenticator (platform secure
 *     enclave / TPM) and is never exposed to the renderer. We only
 *     get the 32-byte output after the biometric check succeeds.
 *   - The wrapped passphrase sits at rest in IndexedDB, unsealed but
 *     useless without the PRF output. A filesystem-grabber without
 *     biometric access cannot unwrap it.
 *   - The passphrase itself stays the primary unlock path. Biometric
 *     is additive — forgetting the passphrase means re-register. A
 *     device without PRF support (e.g. older Safari) falls through
 *     to the passphrase prompt seamlessly.
 *   - Loss of the enrolled authenticator (new device, reset) is
 *     survivable: passphrase entry still works.
 *
 * Browser support: Chrome 128+, Edge 128+, Safari 18+. Firefox does
 * not yet support PRF; the `isWebAuthnPrfAvailable` feature-check
 * returns false there and the UI falls back to passphrase-only.
 */
import { fromBase64Url, toBase64Url } from '@chat/protocol';
import type { KeyStore } from '@chat/crypto';

// NOTE: storage key + PRF info context deliberately stay on the
// "chat" namespace — they're part of the credential binding and
// changing them would invalidate existing biometric enrollments.
// Only user-facing display strings (RP_NAME / USER_DISPLAY) pick up
// the Gamez rebrand.
export const BIOMETRIC_KEY = 'chat:biometric:v1';
const RP_NAME = 'Gamez';
const USER_NAME = 'gamez-user';
const USER_DISPLAY = 'Gamez';
const PRF_INFO_CONTEXT = new TextEncoder().encode('chat/biometric-wrap/v1');

interface StoredBiometric {
  readonly version: 1;
  /** base64url of the WebAuthn credential's rawId. */
  readonly credentialId: string;
  /** base64url of the PRF eval input (32 bytes). */
  readonly prfSalt: string;
  /** base64url of the AES-GCM nonce (12 bytes). */
  readonly nonce: string;
  /** base64url of AES-256-GCM(wrappedPassphrase, prfOutput). */
  readonly wrappedPassphrase: string;
}

function parseStored(bytes: Uint8Array): StoredBiometric | undefined {
  try {
    const record = JSON.parse(new TextDecoder().decode(bytes)) as StoredBiometric;
    if (record.version !== 1) return undefined;
    return record;
  } catch {
    return undefined;
  }
}

function writeStored(raw: KeyStore, record: StoredBiometric): Promise<void> {
  return raw.put(BIOMETRIC_KEY, new TextEncoder().encode(JSON.stringify(record)));
}

function copyToBuffer(bytes: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(bytes.length);
  new Uint8Array(out).set(bytes);
  return out;
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

interface CredentialsNavigator {
  readonly credentials?: CredentialsContainer;
}

interface ExtensionResultsWithPrf {
  readonly prf?: {
    readonly enabled?: boolean;
    readonly results?: { readonly first?: ArrayBuffer };
  };
}

function getPrfResult(ext: unknown): ArrayBuffer | undefined {
  const withPrf = ext as ExtensionResultsWithPrf | undefined;
  const first = withPrf?.prf?.results?.first;
  return first instanceof ArrayBuffer ? first : undefined;
}

/**
 * True when the runtime exposes `navigator.credentials` and platform
 * authenticators. Not a guarantee that PRF is available — we only
 * learn that after a real `create()` — but a fast negative check
 * that lets the UI know whether to offer the biometric option at all.
 */
export async function isWebAuthnPrfAvailable(): Promise<boolean> {
  const nav = (globalThis as unknown as { navigator?: CredentialsNavigator }).navigator;
  if (!nav?.credentials?.create || !nav.credentials.get) return false;
  const maybe = globalThis as unknown as {
    PublicKeyCredential?: {
      isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
    };
  };
  const check = maybe.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable;
  if (typeof check !== 'function') return false;
  try {
    return await check();
  } catch {
    return false;
  }
}

export async function hasBiometricEnrolled(raw: KeyStore): Promise<boolean> {
  const bytes = await raw.get(BIOMETRIC_KEY);
  if (!bytes) return false;
  return parseStored(bytes) !== undefined;
}

export async function clearBiometric(raw: KeyStore): Promise<void> {
  await raw.delete(BIOMETRIC_KEY);
}

/**
 * Enroll a platform authenticator credential that can wrap the
 * passphrase under a PRF-derived AES key. Throws if PRF is not
 * supported or the user cancels / the authenticator is unavailable.
 */
export async function enrollBiometric(raw: KeyStore, passphrase: string): Promise<void> {
  if (!passphrase) throw new Error('passphrase required');
  const nav = (globalThis as unknown as { navigator?: CredentialsNavigator }).navigator;
  if (!nav?.credentials?.create || !nav.credentials.get) {
    throw new Error('WebAuthn unavailable');
  }

  const prfSalt = randomBytes(32);
  const userId = randomBytes(16);
  const createChallenge = randomBytes(32);

  // The PRF extension requires `residentKey: required` on at least
  // one major platform; platform attachment ensures we get Touch ID /
  // Face ID / Windows Hello rather than a roaming authenticator.
  const credential = (await nav.credentials.create({
    publicKey: {
      rp: { name: RP_NAME },
      user: {
        id: copyToBuffer(userId),
        name: USER_NAME,
        displayName: USER_DISPLAY,
      },
      challenge: copyToBuffer(createChallenge),
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 }, // ES256
        { type: 'public-key', alg: -257 }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        residentKey: 'required',
        userVerification: 'required',
      },
      timeout: 60_000,
      extensions: {
        prf: { eval: { first: copyToBuffer(prfSalt) } },
      } as unknown as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('credential creation failed');

  // Prefer the PRF output returned on create when the platform
  // provides it. Fall back to a get() otherwise; WebAuthn spec leaves
  // create-time PRF as "may return".
  const createExt = credential.getClientExtensionResults();
  let prfOutput = getPrfResult(createExt);
  if (!prfOutput) {
    const probe = (await nav.credentials.get({
      publicKey: {
        challenge: copyToBuffer(randomBytes(32)),
        allowCredentials: [
          {
            type: 'public-key',
            id: credential.rawId,
            transports: ['internal'],
          },
        ],
        userVerification: 'required',
        timeout: 60_000,
        extensions: {
          prf: { eval: { first: copyToBuffer(prfSalt) } },
        } as unknown as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null;
    if (!probe) throw new Error('PRF probe failed');
    prfOutput = getPrfResult(probe.getClientExtensionResults());
    if (!prfOutput) {
      throw new Error('authenticator does not support PRF');
    }
  }

  const key = await crypto.subtle.importKey(
    'raw',
    prfOutput,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );
  const nonce = randomBytes(12);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: copyToBuffer(nonce), additionalData: PRF_INFO_CONTEXT },
      key,
      copyToBuffer(new TextEncoder().encode(passphrase)),
    ),
  );

  await writeStored(raw, {
    version: 1,
    credentialId: toBase64Url(new Uint8Array(credential.rawId)),
    prfSalt: toBase64Url(prfSalt),
    nonce: toBase64Url(nonce),
    wrappedPassphrase: toBase64Url(ciphertext),
  });
}

/**
 * Retrieve the enrolled credential, prompt biometrics, derive the
 * PRF output, and unwrap the stored passphrase. The returned string
 * is passed to `openSealedStore` / `unlockSealedStore` by the caller.
 *
 * Any failure (no record, user cancels, wrong authenticator, PRF
 * mismatch) throws — the UI should fall back to passphrase entry.
 */
export async function unlockWithBiometric(raw: KeyStore): Promise<string> {
  const bytes = await raw.get(BIOMETRIC_KEY);
  const record = bytes ? parseStored(bytes) : undefined;
  if (!record) throw new Error('no biometric credential enrolled');
  const nav = (globalThis as unknown as { navigator?: CredentialsNavigator }).navigator;
  if (!nav?.credentials?.get) throw new Error('WebAuthn unavailable');

  const credential = (await nav.credentials.get({
    publicKey: {
      challenge: copyToBuffer(randomBytes(32)),
      allowCredentials: [
        {
          type: 'public-key',
          id: copyToBuffer(fromBase64Url(record.credentialId)),
          transports: ['internal'],
        },
      ],
      userVerification: 'required',
      timeout: 60_000,
      extensions: {
        prf: { eval: { first: copyToBuffer(fromBase64Url(record.prfSalt)) } },
      } as unknown as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;
  if (!credential) throw new Error('biometric unlock failed');

  const prfOutput = getPrfResult(credential.getClientExtensionResults());
  if (!prfOutput) throw new Error('authenticator did not return PRF output');

  const key = await crypto.subtle.importKey(
    'raw',
    prfOutput,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );
  const plaintext = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: copyToBuffer(fromBase64Url(record.nonce)),
      additionalData: PRF_INFO_CONTEXT,
    },
    key,
    copyToBuffer(fromBase64Url(record.wrappedPassphrase)),
  );
  return new TextDecoder().decode(plaintext);
}
